/**
 * Profile Service
 *
 * Handles profile-related API requests
 */

import { apiClient, ApiResponse } from './apiClient';
import { csrfService } from './csrfService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8008/api';
const TOKEN_KEY = 'fitbook_token';

export interface ProfileFormData {
  // Basic info
  firstname: string;
  lastname: string;
  phone?: string;

  // Location (HubSpot standard properties)
  country: string;
  state?: string;
  city: string;
  address?: string;

  // Certification
  escuela_de_formacion: string;
  ano_de_formacion: string;
  maestro_de_la_formacion?: string;

  // Files (will be handled separately)
  foto?: File;
  certificado?: File;

  // Professional development
  desarrollo_personal?: boolean;
  // Specialties - array of valid values for HubSpot multi-checkbox
  specialties?: string[];

  // Materials DAI - maps to HubSpot material_dai multi-checkbox
  has_crystals_bed?: boolean;    // "Cama de cristales"
  has_arrow?: boolean;           // "Flechas"
  has_steel_bars?: boolean;      // "Barras de acero"
  has_concrete_blocks?: boolean; // "Bloques hormigón"
  has_other?: boolean;           // "Otro"
  complemento_material_dai?: string; // Text description if "Otro" selected

  // Suppliers - maps to HubSpot soporte_proveedores_material multi-checkbox
  provides_wood?: boolean;           // "Leña"
  provides_tepe?: boolean;           // "Tepe"
  provides_iron_bar?: boolean;       // "Barras hierro"
  provides_arrows?: boolean;         // "Flechas"
  provides_wood_board?: boolean;     // "Tablas"
  provides_concrete_block?: boolean; // "Bloques de hormigón"

  // Experience
  nivel_de_experiencia_en_fuego?: string;
  cuentanos_tu_experiencia?: string;
}

// Valid specialties for perfil_profesional_desarrollo_personal
export const VALID_SPECIALTIES = [
  'Formador',
  'Speaker (eventos)',
  'Terapeuta (Psicólogo, energético, holístico)',
  'Otros',
  'Ninguno'
] as const;

// Valid values for nivel_de_experiencia_en_fuego
export const VALID_NIVEL_EXPERIENCIA = [
  'Cerilla',
  'Brasero',
  'Horno',
  'Vulcano'
] as const;

export interface ProfileSubmitResponse {
  status: string;
  hubspot_contact_id: string;
}

class ProfileService {
  /**
   * Submit instructor profile with files
   */
  async submitProfile(profileData: ProfileFormData): Promise<ApiResponse<ProfileSubmitResponse>> {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      throw new Error('Authentication required');
    }

    // Create FormData for multipart upload
    const formData = new FormData();

    console.log('[ProfileService] Building FormData from profileData');

    // Add all profile fields to FormData
    Object.entries(profileData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Handle files separately
        if (value instanceof File) {
          console.log(`[ProfileService] Adding file field "${key}":`, {
            name: value.name,
            type: value.type,
            size: value.size
          });
          formData.append(key, value, value.name);
        }
        // Handle booleans
        else if (typeof value === 'boolean') {
          formData.append(key, value ? 'true' : 'false');
        }
        // Handle arrays (como specialties) - join with semicolon for HubSpot multi-checkbox
        else if (Array.isArray(value)) {
          formData.append(key, value.join(';'));
        }
        // Handle strings and numbers
        else {
          formData.append(key, String(value));
        }
      }
    });

    console.log('[ProfileService] FormData built successfully');

    try {
      const url = `${API_BASE_URL}/profile/submit`;
      console.log('[ProfileService] Sending profile submission to:', url);

      // Add CSRF token
      const csrfToken = await csrfService.getToken();
      const headers: HeadersInit = {
        'Authorization': `Bearer ${token}`,
        'X-CSRF-Token': csrfToken,
        // Don't set Content-Type header, browser will set it with boundary for multipart
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: formData,
        credentials: 'include', // Important for CSRF
      });

      console.log('[ProfileService] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      // Parse JSON response
      let data: ApiResponse<ProfileSubmitResponse>;
      try {
        data = await response.json();
        console.log('[ProfileService] Response data:', data);
      } catch (parseError) {
        console.error('[ProfileService] Error parsing response JSON:', parseError);
        data = {
          success: false,
          error: 'Error al parsear la respuesta del servidor',
        };
      }

      // Handle 401 Unauthorized
      if (response.status === 401) {
        console.warn('[ProfileService] Unauthorized - removing token');
        localStorage.removeItem(TOKEN_KEY);
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        throw new Error(data.error || 'No autorizado');
      }

      // Handle other HTTP errors
      if (!response.ok) {
        console.error('[ProfileService] HTTP error:', {
          status: response.status,
          statusText: response.statusText,
          errorMessage: data.error
        });
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('[ProfileService] Profile submitted successfully');
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  /**
   * Get profile status
   */
  async getStatus(): Promise<ApiResponse> {
    return apiClient.get('/profile/status', true);
  }

  /**
   * Get own profile data (for active users)
   */
  async getMyData(): Promise<ApiResponse<any>> {
    return apiClient.get<any>('/profile/my-data', true);
  }

  /**
   * Update profile (for active users)
   */
  async updateProfile(data: Partial<ProfileFormData>): Promise<ApiResponse> {
    return apiClient.put('/profile/update', data, true);
  }
}

export const profileService = new ProfileService();
